import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import WifiIcon from "@mui/icons-material/Wifi";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { layoutCSS } from "../component/common/commonStyle";
import { usePosttruecallerTemplateMutation } from "../store/services/trucallerApi";

// ─── Config ───────────────────────────────────────────────────────────────────
const MAX_BUTTONS = 4; // change if Truecaller allows a different number
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB

const CATEGORY_OPTIONS = ["PROMO", "Trans", "OTP"];
const TEMPLATE_CATEGORY_OPTIONS = ["Marketing", "Support"];
const ACTION_TAG_OPTIONS = ["website_visit", "view_offer"];

// ─── Theme ────────────────────────────────────────────────────────────────────
const PURPLE = "#7c3aed";
const PURPLE_LIGHT = "#c4b5fd";

const glass = {
  background: "rgba(255,255,255,0.55)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255,255,255,0.65)",
  boxShadow: "0 2px 10px rgba(124,58,237,0.06)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.7)",
    borderRadius: "8px",
    fontSize: "0.78rem",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d6cdf3",
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: PURPLE_LIGHT },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: PURPLE,
      borderWidth: "1px",
    },
  },
  "& .MuiInputLabel-root": { fontSize: "0.78rem", color: "#64748B" },
  "& .MuiInputLabel-root.Mui-focused": { color: PURPLE },
  "& .MuiInputBase-input": { fontSize: "0.78rem" },
  "& .MuiSelect-select": { fontSize: "0.78rem" },
  "& .MuiFormHelperText-root": {
    fontSize: "0.65rem",
    marginLeft: 0,
    marginTop: "2px",
  },
};

// Compact single-line inputs / dropdowns
const smallSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    minHeight: 34,
  },
  "& .MuiInputBase-input": { fontSize: "0.78rem", py: "7px" },
  "& .MuiSelect-select": { fontSize: "0.78rem", py: "7px" },
};

const menuItemSx = { fontSize: "0.78rem", minHeight: 28 };

const outlinedPurpleBtn = {
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 600,
  height: 32,
  px: 1.5,
  borderRadius: "8px",
  borderColor: PURPLE_LIGHT,
  color: PURPLE,
  background: "rgba(255,255,255,0.6)",
  "&:hover": { borderColor: PURPLE, background: "rgba(124,58,237,0.06)" },
  "&.Mui-disabled": { borderColor: "#e2e8f0", color: "#94a3b8" },
};

const SectionCard = ({ title, children, sx }) => (
  <Paper
    elevation={0}
    sx={{ ...glass, p: 1.5, mb: 1.25, borderRadius: "12px", ...sx }}
  >
    {title && (
      <Typography
        sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#0F172A", mb: 1 }}
      >
        {title}
      </Typography>
    )}
    {children}
  </Paper>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const isValidUrl = (u) => {
  try {
    const p = new URL(u);
    return p.protocol === "http:" || p.protocol === "https:";
  } catch {
    return false;
  }
};

const getVariableIndices = (text = "") => {
  const matches = [...String(text || "").matchAll(/\{\{(\d+)\}\}/g)];
  return [...new Set(matches.map((m) => Number(m[1])))].sort((a, b) => a - b);
};

const getNextVariableIndex = (text = "") => {
  const indices = getVariableIndices(text);
  return indices.length ? Math.max(...indices) + 1 : 1;
};

const makeQuickReply = () => ({ type: "quickReply", text: "" });
const makeActionButton = () => ({
  type: "action",
  text: "",
  action_tag: "website_visit",
  uri: "",
});

// ─── Component ────────────────────────────────────────────────────────────────
function TruecallerTemplate() {
  const navigate = useNavigate();
  const [postTemplate, { isLoading }] = usePosttruecallerTemplateMutation();

  const [templateName, setTemplateName] = useState("");
  const [category, setCategory] = useState("PROMO");
  const [templateType, setTemplateType] = useState("text");
  const [templateCategory, setTemplateCategory] = useState("Marketing");
  const [msgdata, setMsgdata] = useState("");
  const [samples, setSamples] = useState({}); // preview only, never sent
  const [mediaFile, setMediaFile] = useState(null);
  const [buttons, setButtons] = useState([]);

  // Clock shown in the phone status bar
  const [clock] = useState(() =>
    new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
  );

  // Preview URL for the uploaded image (revoked on change / unmount)
  const previewUrl = useMemo(
    () => (mediaFile ? URL.createObjectURL(mediaFile) : ""),
    [mediaFile],
  );
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // ─── Button helpers ─────────────────────────────────────────────────────────
  const addButton = (type) => {
    if (buttons.length >= MAX_BUTTONS) return;
    setButtons((prev) => [
      ...prev,
      type === "quickReply" ? makeQuickReply() : makeActionButton(),
    ]);
  };

  const removeButton = (index) =>
    setButtons((prev) => prev.filter((_, i) => i !== index));

  const updateButton = (index, field, value) =>
    setButtons((prev) =>
      prev.map((b, i) => (i === index ? { ...b, [field]: value } : b)),
    );

  const changeButtonType = (index, type) =>
    setButtons((prev) =>
      prev.map((b, i) =>
        i === index
          ? type === "quickReply"
            ? makeQuickReply()
            : makeActionButton()
          : b,
      ),
    );

  // ─── File handler ───────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error("Image size cannot exceed 5 MB");
      e.target.value = "";
      return;
    }
    setMediaFile(file);
  };

  const handleTypeChange = (value) => {
    setTemplateType(value);
    if (value === "text") setMediaFile(null);
  };

  // ─── Preview text (variables replaced with sample values) ───────────────────
  const variableIndices = getVariableIndices(msgdata);
  const previewText = msgdata.replace(/\{\{(\d+)\}\}/g, (match, n) => {
    const val = (samples[n] || "").trim();
    return val || match;
  });

  const hasButtonLabels = buttons.some((b) => b.text?.trim());
  const hasPreviewContent =
    !!msgdata.trim() || (templateType === "media" && !!previewUrl) || hasButtonLabels;

  // ─── Validation ─────────────────────────────────────────────────────────────
  const validateForm = () => {
    if (!templateName.trim()) return "Template name is required";
    if (!category) return "Category is required";
    if (!templateType) return "Template type is required";
    if (!templateCategory.trim()) return "Template category is required";
    if (!msgdata.trim()) return "Message is required";
    if (templateType === "media" && !mediaFile)
      return "Please upload a media file for a media template";

    for (const [i, b] of buttons.entries()) {
      if (!b.text.trim()) return `Button ${i + 1}: text is required`;
      if (b.type === "action") {
        if (!b.action_tag.trim()) return `Button ${i + 1}: action tag is required`;
        if (!isValidUrl(b.uri.trim()))
          return `Button ${i + 1}: enter a valid URL (http/https)`;
      }
    }
    return "";
  };

  // ─── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("template_name", templateName.trim());
      formData.append("category", category);
      formData.append("template_type", templateType);
      formData.append("template_category", templateCategory.trim());
      formData.append("msgdata", msgdata);

      // Quick reply -> plain string, action button -> object
      const buttonsPayload = buttons.map((b) =>
        b.type === "quickReply"
          ? b.text.trim()
          : {
              text: b.text.trim(),
              action_tag: b.action_tag.trim(),
              uri: b.uri.trim(),
            },
      );
      if (buttonsPayload.length > 0)
        formData.append("buttons", JSON.stringify(buttonsPayload));

      if (templateType === "media" && mediaFile)
        formData.append("media", mediaFile);

      await postTemplate(formData).unwrap();
      toast.success("Template submitted successfully!");
      setTimeout(() => navigate(-1), 1000);
    } catch (err) {
      console.log("Truecaller template error:", err);
      const apiMessage =
        err?.data?.error?.message ||
        (typeof err?.data?.error === "string" ? err.data.error : null) ||
        err?.data?.message ||
        (typeof err?.error === "string" ? err.error : null) ||
        "Template creation failed";
      toast.error(String(apiMessage), { autoClose: 8000 });
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ ...layoutCSS, p: 1.5 }}>
      {/* Title bar */}
      <Paper
        elevation={0}
        sx={{
          ...glass,
          display: "flex",
          alignItems: "center",
          px: 1.5,
          py: 1,
          mb: 1.5,
          borderRadius: "12px",
        }}
      >
        <IconButton onClick={() => navigate(-1)} size="small" sx={{ mr: 0.75, p: 0.5 }}>
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1e293b" }}>
          Create Template
        </Typography>
      </Paper>

      <Grid container spacing={1.5}>
        {/* ── Left: form panel ── */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              ...glass,
              background: "rgba(255,255,255,0.4)",
              p: 1.5,
              borderRadius: "16px",
            }}
          >
            {/* Template details */}
            <SectionCard title="Template Details">
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start" }}>
                <TextField
                  required
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  size="small"
                  label="Template Name"
                  sx={{ ...smallSx, width: 210 }}
                />
                <FormControl required size="small" sx={{ ...smallSx, width: 150 }}>
                  <InputLabel id="tc-category-label">Category</InputLabel>
                  <Select
                    labelId="tc-category-label"
                    label="Category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {CATEGORY_OPTIONS.map((c) => (
                      <MenuItem key={c} value={c} sx={menuItemSx}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl required size="small" sx={{ ...smallSx, width: 120 }}>
                  <InputLabel id="tc-type-label">Type</InputLabel>
                  <Select
                    labelId="tc-type-label"
                    label="Type"
                    value={templateType}
                    onChange={(e) => handleTypeChange(e.target.value)}
                  >
                    <MenuItem value="text" sx={menuItemSx}>Text</MenuItem>
                    <MenuItem value="media" sx={menuItemSx}>Media</MenuItem>
                  </Select>
                </FormControl>
                <FormControl required size="small" sx={{ ...smallSx, width: 140 }}>
                  <InputLabel id="tc-template-category-label">Template Category</InputLabel>
                  <Select
                    labelId="tc-template-category-label"
                    label="Template Category"
                    value={templateCategory}
                    onChange={(e) => setTemplateCategory(e.target.value)}
                  >
                    {TEMPLATE_CATEGORY_OPTIONS.map((c) => (
                      <MenuItem key={c} value={c} sx={menuItemSx}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </SectionCard>

            {/* Upload + Message side by side */}
            <Box
              sx={{
                display: "flex",
                gap: 1.25,
                alignItems: "stretch",
                mb: 1.25,
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              {templateType === "media" && (
                <SectionCard
                  title="Upload Media"
                  sx={{
                    mb: 0,
                    flex: { xs: "1 1 100%", sm: "0 0 30%" },
                    maxWidth: { xs: "100%", sm: "30%" },
                    minWidth: { xs: 0, sm: 150 },
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
                      sx={{ ...outlinedPurpleBtn, width: "100%" }}
                    >
                      Upload
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleFileChange}
                      />
                    </Button>

                    {previewUrl && (
                      <Box
                        component="img"
                        src={previewUrl}
                        alt="Media preview"
                        sx={{
                          width: "100%",
                          maxHeight: 96,
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "1px solid #e5defa",
                          display: "block",
                        }}
                      />
                    )}

                    {mediaFile?.name ? (
                      <Typography
                        noWrap
                        title={mediaFile.name}
                        sx={{ fontSize: "0.72rem", color: "#475569" }}
                      >
                        {mediaFile.name}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: "0.7rem", color: "#dc2626" }}>
                        Image only, max 5 MB
                      </Typography>
                    )}
                  </Box>
                </SectionCard>
              )}

              <SectionCard
                title="Message"
                sx={{
                  mb: 0,
                  flex: "1 1 auto",
                  minWidth: { xs: 0, sm: 240 },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <TextField
                  required
                  value={msgdata}
                  onChange={(e) => setMsgdata(e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  label="Message (msgdata)"
                  sx={fieldSx}
                  helperText={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() =>
                          setMsgdata(
                            (msgdata || "") + `{{${getNextVariableIndex(msgdata)}}}`,
                          )
                        }
                        sx={{
                          textTransform: "none",
                          fontSize: "0.68rem",
                          minWidth: 0,
                          p: 0,
                          lineHeight: 1.4,
                          color: PURPLE,
                        }}
                      >
                        + Add Variable
                      </Button>
                      <Typography sx={{ fontSize: "0.68rem" }}>
                        {msgdata.length} characters
                      </Typography>
                    </Box>
                  }
                />

                {variableIndices.length > 0 && (
                  <Box
                    sx={{
                      mt: 0.75,
                      p: 1,
                      borderRadius: "8px",
                      border: "1px dashed #d6cdf3",
                      background: "rgba(255,255,255,0.5)",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.68rem", color: "#64748B", mb: 0.5 }}>
                      Preview only, not sent with the template.
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {variableIndices.map((i) => (
                        <TextField
                          key={i}
                          size="small"
                          label={`{{${i}}} sample`}
                          placeholder="e.g. Rahul"
                          value={samples[i] || ""}
                          onChange={(e) =>
                            setSamples((prev) => ({ ...prev, [i]: e.target.value }))
                          }
                          sx={{ ...smallSx, width: 140 }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </SectionCard>
            </Box>

            {/* Buttons */}
            <SectionCard title={`Buttons (${buttons.length}/${MAX_BUTTONS})`}>
              <Stack spacing={0.75}>
                {buttons.map((btn, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                      gap: 1,
                      p: 1,
                      background: "rgba(255,255,255,0.5)",
                      borderRadius: "8px",
                      border: "1px solid #e5defa",
                    }}
                  >
                    <FormControl size="small" sx={{ ...smallSx, width: 130 }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        label="Type"
                        value={btn.type}
                        onChange={(e) => changeButtonType(index, e.target.value)}
                      >
                        <MenuItem value="quickReply" sx={menuItemSx}>Quick Reply</MenuItem>
                        <MenuItem value="action" sx={menuItemSx}>Action Button</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      size="small"
                      label="Button Text"
                      value={btn.text}
                      onChange={(e) => updateButton(index, "text", e.target.value)}
                      sx={{ ...smallSx, width: 160 }}
                    />

                    {btn.type === "action" && (
                      <>
                        <Autocomplete
                          freeSolo
                          size="small"
                          options={ACTION_TAG_OPTIONS}
                          inputValue={btn.action_tag}
                          onInputChange={(_, value) =>
                            updateButton(index, "action_tag", value)
                          }
                          sx={{ width: 150 }}
                          slotProps={{ paper: { sx: { fontSize: "0.78rem" } } }}
                          renderInput={(params) => (
                            <TextField {...params} label="Action Tag" sx={smallSx} />
                          )}
                        />
                        <TextField
                          size="small"
                          label="URL (uri)"
                          placeholder="https://example.com"
                          value={btn.uri}
                          onChange={(e) => updateButton(index, "uri", e.target.value)}
                          sx={{ ...smallSx, width: 230 }}
                        />
                      </>
                    )}

                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeButton(index)}
                      sx={{ p: 0.5, mt: "3px" }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>

              <Box sx={{ display: "flex", gap: 1, mt: buttons.length ? 0.75 : 0 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  disabled={buttons.length >= MAX_BUTTONS}
                  onClick={() => addButton("quickReply")}
                  sx={outlinedPurpleBtn}
                >
                  Quick Reply
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  disabled={buttons.length >= MAX_BUTTONS}
                  onClick={() => addButton("action")}
                  sx={outlinedPurpleBtn}
                >
                  Action Button
                </Button>
              </Box>
            </SectionCard>

            {/* Submit / Cancel on the left */}
            <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
              <Button
                variant="contained"
                disabled={isLoading}
                onClick={handleSubmit}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  height: 32,
                  px: 2.5,
                  borderRadius: "20px",
                  background: "#7c6cf6",
                  boxShadow: "none",
                  "&:hover": { background: PURPLE, boxShadow: "none" },
                }}
              >
                {isLoading ? "Submitting..." : "Submit Template"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  ...outlinedPurpleBtn,
                  borderRadius: "20px",
                  px: 2.5,
                }}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* ── Right: phone preview ── */}
        <Grid item xs={12} md={4}>
          <Box sx={{ position: "sticky", top: 12 }}>
            <Box
              sx={{
                width: 260,
                height: 470,
                margin: "0 auto",
                border: "3px solid #0f172a",
                borderRadius: "28px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                background: "#f4f4f5",
                boxShadow: "0 10px 28px rgba(15,23,42,0.25)",
              }}
            >
              {/* Status bar */}
              <Box
                sx={{
                  height: 28,
                  px: 1.75,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "#0f172a",
                }}
              >
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#fbbf24" }}>
                  {clock}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <WifiIcon sx={{ fontSize: 13, color: "#e2e8f0" }} />
                  <SignalCellularAltIcon sx={{ fontSize: 13, color: "#e2e8f0" }} />
                  <BatteryFullIcon sx={{ fontSize: 13, color: "#e2e8f0" }} />
                </Stack>
              </Box>

              {/* Chat header */}
              <Box
                sx={{
                  height: 42,
                  px: 1.25,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  background: "#f1f5f9",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 16, color: "#cbd5e1" }} />
                <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#0f172a" }}>
                  Business Message
                </Typography>
              </Box>

              {/* Body */}
              <Box
                sx={{
                  flex: 1,
                  p: 1.25,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  background: "#f4f4f5",
                }}
              >
                {!hasPreviewContent ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "4px",
                        background: "#cbd5e1",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        color: "#334155",
                        textAlign: "center",
                        lineHeight: 1.5,
                      }}
                    >
                      Select template
                      <br />
                      to preview
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        background: "#fff",
                        p: 0.75,
                        borderRadius: "10px",
                        border: "1px solid #e2e8f0",
                        wordWrap: "break-word",
                      }}
                    >
                      {templateType === "media" && previewUrl && (
                        <img
                          src={previewUrl}
                          alt="Media preview"
                          style={{
                            width: "100%",
                            borderRadius: "6px",
                            marginBottom: "4px",
                            display: "block",
                          }}
                        />
                      )}
                      {previewText && (
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            lineHeight: 1.4,
                            whiteSpace: "pre-line",
                            px: 0.5,
                            color: "#0f172a",
                          }}
                        >
                          {previewText}
                        </Typography>
                      )}
                    </Box>

                    {buttons.map((btn, i) => {
                      const label = btn.text?.trim();
                      if (!label) return null;
                      return (
                        <Button
                          key={i}
                          variant="contained"
                          size="small"
                          fullWidth
                          sx={{
                            mt: 0.75,
                            py: 0.35,
                            fontSize: "0.75rem",
                            background: "#fff",
                            color: "#2563eb",
                            border: "1px solid #e2e8f0",
                            textTransform: "none",
                            boxShadow: "none",
                            "&:hover": { background: "#f8fafc", boxShadow: "none" },
                          }}
                          onClick={() => {
                            if (btn.type === "action" && isValidUrl(btn.uri)) {
                              window.open(btn.uri, "_blank");
                            }
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default TruecallerTemplate;